import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex gap-3 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.cover}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
            title={comic.title}
          />
        </div>
        <div className="flex flex-col justify-between min-w-0">
          <div>
            <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
              {comic.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span className="flex items-center gap-0.5">
              <i className="ri-star-fill text-accent-500 text-[10px]" />
              {comic.rating}
            </span>
            <span>{formatViews(comic.views)} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={comic.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          title={comic.title}
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
            comic.status === '连载中'
              ? 'bg-primary-500 text-white'
              : 'bg-secondary-500 text-white'
          }`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
          <div className="flex items-center gap-1 text-white text-xs">
            <i className="ri-eye-line" />
            <span>{formatViews(comic.views)}</span>
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <div className="flex items-center justify-between mt-1.5">
          <p className="text-xs text-foreground-500">{comic.author}</p>
          <span className="flex items-center gap-0.5 text-xs text-accent-600">
            <i className="ri-star-fill text-[10px]" />
            {comic.rating}
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {comic.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}